import React from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";

interface Feature {
  icon: string;
  title: string;
  description: string;
  gradient: string;
}

const features: Feature[] = [
  {
    icon: "inventory_2",
    title: "Advanced Inventory & Batch Management",
    description:
      "Track every raw material by batch, expiry date, and cost. Get precise control over what's used, wasted, or about to expire.",
    gradient: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
  },
  {
    icon: "restaurant_menu",
    title: "Smart Recipe & API Management",
    description:
      "Craft recipes with full flexibility, using real-time ingredient costs and alternate options when stock is unavailable. Create processed items (APIs) with expiry tracking and cost history.",
    gradient: "linear-gradient(135deg, #0891B2 0%, #0E7490 100%)",
  },
  {
    icon: "receipt_long",
    title: "Bill of Materials for Sellable Items",
    description:
      "Build and manage sellable items using your own Bill of Materials templates. Design your menu with accuracy and total cost awareness.",
    gradient: "linear-gradient(135deg, #0E7490 0%, #155E75 100%)",
  },
  {
    icon: "point_of_sale",
    title: "POS Integrated with Kitchen & Inventory",
    description:
      "Real-time sync between cashier activity and backend stock. Every sale reflects instantly on inventory, costs, and batch usage.",
    gradient: "linear-gradient(135deg, #155E75 0%, #164E63 100%)",
  },
  {
    icon: "account_tree",
    title: "Multi-Branch & Multi-Warehouse Support",
    description:
      "Add unlimited branches, departments, and warehouses. Control access, stock movement, and reporting at every level.",
    gradient: "linear-gradient(135deg, #164E63 0%, #083344 100%)",
  },
  {
    icon: "manage_accounts",
    title: "Staff Access & Role Management",
    description:
      "Define who can access what. From branch managers to warehouse handlers, customize access and actions with precision.",
    gradient: "linear-gradient(135deg, #083344 0%, #082F49 100%)",
  },
  {
    icon: "insights",
    title: "Cost Tracking & Optimization",
    description:
      "Understand true product costs with ingredient-level breakdowns. Get insights on most expensive and cheapest recipe versions.",
    gradient: "linear-gradient(135deg, #082F49 0%, #172554 100%)",
  },
  {
    icon: "fact_check",
    title: "Activity Logs & Operational Transparency",
    description:
      "Track every change in recipes, stock, and settings. Know who did what, when, and why—for full traceability and accountability.",
    gradient: "linear-gradient(135deg, #172554 0%, #1E1B4B 100%)",
  },
];

const FeatureCard: React.FC<{
  icon: string;
  title: string;
  description: string;
  gradient: string;
  index: number;
}> = ({ icon, title, description, gradient, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      style={{ height: "100%" }}
    >
      <Box
        sx={{
          p: 4,
          height: "100%",
          background: "rgba(255, 255, 255, 0.95)",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          position: "relative",
          overflow: "hidden",
          transition: "transform 0.3s ease",
          display: "flex",
          flexDirection: "column",
          "&:hover": {
            transform: "translateY(-4px)",
            "& .feature-icon": {
              transform: "scale(1.1)",
            },
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: gradient,
          },
        }}
      >
        <Box
          className="feature-icon"
          sx={{
            width: "48px",
            height: "48px",
            borderRadius: "12px",
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
            transition: "transform 0.3s ease",
            flexShrink: 0,
          }}
        >
          <span
            className="material-icons"
            style={{ color: "white", fontSize: "24px" }}
          >
            {icon}
          </span>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Montserrat",
            fontWeight: 600,
            fontSize: "1.125rem",
            mb: 2,
            color: "#1A2027",
            flexShrink: 0,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: "#4A5568",
            fontSize: "0.875rem",
            lineHeight: 1.6,
            fontFamily: "Montserrat",
            flex: 1,
          }}
        >
          {description}
        </Typography>
      </Box>
    </motion.div>
  );
};

export const FeaturesSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      sx={{
        py: { xs: 10, md: 15 },
        background: "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 50% 50%, rgba(6, 182, 212, 0.03) 0%, transparent 70%)",
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: "center",
              mb: 3,
              color: "#1A2027",
              fontWeight: 700,
              fontFamily: "Montserrat",
              fontSize: { xs: "2rem", md: "2.75rem" },
              maxWidth: "800px",
              mx: "auto",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: -16,
                left: "50%",
                transform: "translateX(-50%)",
                width: "80px",
                height: "3px",
                background: "linear-gradient(90deg, #06B6D4, #22D3EE)",
                borderRadius: "2px",
              },
            }}
          >
            Powerful Features for Your Business
          </Typography>
          <Typography
            sx={{
              textAlign: "center",
              mb: 8,
              color: "#4A5568",
              maxWidth: "700px",
              mx: "auto",
              fontFamily: "Montserrat",
              fontSize: { xs: "1rem", md: "1.125rem" },
              lineHeight: 1.6,
              mt: 4,
            }}
          >
            Discover our comprehensive suite of tools designed to streamline
            your operations and boost efficiency across your entire business.
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <FeatureCard {...feature} index={index} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};
